import { useEffect, useRef, useState } from 'react'
import { useT } from '@/shared/i18n/useT'

/**
 * Resize corner handle for floating menus (grid header filter, cell dropdowns,
 * client picker, etc.): the user drags the bottom-right corner and the panel
 * maintains the chosen size, remembered in `localStorage`.
 *
 * Used in two parts: the `useResizableSize` hook (state + dragging) and the
 * `ResizeCorner` component (the drag handle with diagonal stripes). The panel
 * should be `flex flex-col` with the scrollable content in a child
 * `min-h-0 flex-1 overflow-y-auto`, ensuring the corner stays pinned at the bottom.
 */

export interface PanelSize { width: number; height: number }
export type TamanoPanel = PanelSize

export interface SizeOptions {
  /** Key in `localStorage` to persist the size. `null` = do not save (e.g. embedded grid). */
  key?: string | null
  minWidth?: number
  minHeight?: number
  /** Backwards compatibility alias */
  clave?: string | null
  minAncho?: number
  minAlto?: number
}
export type OpcionesTamano = SizeOptions

const readStoredSize = (key: string | null): PanelSize | null => {
  if (!key) return null
  try {
    const saved = JSON.parse(localStorage.getItem(key) ?? 'null') as PanelSize | null
    return saved && typeof saved.width === 'number' && typeof saved.height === 'number' ? saved : null
  } catch { return null }
}

/**
 * Size chosen by the user for a floating panel.
 *
 * `initialSize` is the starting size; with `null`, the panel retains its automatic size
 * (determined by content and distance to viewport edge) until the user drags the corner
 * for the first time, starting from its current rendered dimensions.
 */
export function useResizableSize<E extends HTMLElement = HTMLDivElement>(
  initialSize: PanelSize | null = null,
  options: SizeOptions = {},
) {
  const resolvedKey = options.key !== undefined ? options.key : (options.clave ?? null)
  const minWidth = options.minWidth ?? options.minAncho ?? 160
  const minHeight = options.minHeight ?? options.minAlto ?? 120

  const panelRef = useRef<E | null>(null)
  const [size, setSize] = useState<PanelSize | null>(() => readStoredSize(resolvedKey) ?? initialSize)

  useEffect(() => {
    if (resolvedKey && size) localStorage.setItem(resolvedKey, JSON.stringify(size))
  }, [resolvedKey, size])

  const startResize = (event: React.MouseEvent) => {
    // `preventDefault` also stops mousedown from blurring the input that opened the dropdown
    // (several floating menus close on blur).
    event.preventDefault()
    event.stopPropagation()
    const rect = panelRef.current?.getBoundingClientRect()
    const w0 = size?.width ?? rect?.width ?? minWidth
    const h0 = size?.height ?? rect?.height ?? minHeight
    const { clientX: x0, clientY: y0 } = event
    const move = (e: MouseEvent) => setSize({
      width: Math.max(minWidth, w0 + (e.clientX - x0)),
      height: Math.max(minHeight, h0 + (e.clientY - y0)),
    })
    const release = () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', release)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', release)
  }

  /** Back to the automatic size, forgetting the stored one. */
  const resetSize = () => {
    if (resolvedKey) localStorage.removeItem(resolvedKey)
    setSize(null)
  }

  return {
    panelRef,
    size,
    setSize,
    startResize,
    resetSize,
    // Backwards-compatible aliases
    tamano: size,
    setTamano: setSize,
    empezarResize: startResize,
  }
}

export const useTamanoRedimensionable = useResizableSize

/** Drag handle for bottom-right corner resize (see `useResizableSize`). */
export function ResizeCorner({ onMouseDown, onDoubleClick }: {
  onMouseDown: (event: React.MouseEvent) => void
  /** E.g. `resetSize`, to fit the panel to its content again. */
  onDoubleClick?: () => void
}) {
  const t = useT()
  return (
    <span onMouseDown={onMouseDown} onDoubleClick={onDoubleClick} title={t('grid.dragResize')} className="relative h-4 w-full shrink-0 cursor-nwse-resize">
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" className="absolute bottom-0.5 right-0.5 opacity-40 hover:opacity-80">
        <line x1="9" y1="1" x2="1" y2="9" stroke="currentColor" strokeWidth="1" />
        <line x1="9" y1="5" x2="5" y2="9" stroke="currentColor" strokeWidth="1" />
      </svg>
    </span>
  )
}

export const EsquinaTamano = ResizeCorner

